import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "utils/cn";

const badgeVariants = cva(
  "font-inter inline-flex items-center justify-center text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&>svg]:pointer-events-none [&>svg]:w-3 [&>svg]:h-3 [&>svg]:shrink-0 gap-1 whitespace-nowrap shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-gradient-to-r from-primary to-secondary text-primary-foreground border border-primary/20 hover:from-primary/90 hover:to-secondary/90",
        secondary:
          "border border-border bg-muted text-foreground hover:border-border hover:bg-muted/80",
        destructive:
          "bg-danger/10 text-danger border border-danger/20 hover:bg-danger/20 hover:border-danger/30",
        outline:
          "text-primary border border-primary/30 bg-transparent hover:bg-primary/10 hover:border-primary/50",
        success:
          "bg-success/10 text-success border border-success/20 hover:bg-success/20 hover:border-success/30",
        warning:
          "bg-warning/10 text-warning border border-warning/20 hover:bg-warning/20 hover:border-warning/30",
        info: "bg-info/10 text-info border border-info/20 hover:bg-info/20 hover:border-info/30",
        purple:
          "bg-hue-violet/10 text-hue-violet border border-hue-violet/20 hover:bg-hue-violet/15 hover:border-hue-violet/30",
        pending:
          "bg-warning/10 text-warning border border-warning/20 hover:bg-warning/20 hover:border-warning/30",
        owner:
          "bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 hover:border-primary/30",
        admin:
          "bg-hue-blue/10 text-hue-blue border border-hue-blue/20 hover:bg-hue-blue/15 hover:border-hue-blue/30",
        member:
          "bg-hue-emerald/10 text-hue-emerald border border-hue-emerald/20 hover:bg-hue-emerald/15 hover:border-hue-emerald/30",
        viewer:
          "border border-border bg-muted text-muted-foreground hover:border-border hover:bg-muted/80",
        editor:
          "bg-hue-emerald/10 text-hue-emerald border border-hue-emerald/20 hover:bg-hue-emerald/15 hover:border-hue-emerald/30",
        emerald:
          "bg-hue-emerald/10 text-hue-emerald border border-hue-emerald/20 hover:bg-hue-emerald/15 hover:border-hue-emerald/30",
        rose: "bg-hue-rose/10 text-hue-rose border border-hue-rose/20 hover:bg-hue-rose/15 hover:border-hue-rose/30",
        cyan: "bg-hue-cyan/10 text-hue-cyan border border-hue-cyan/20 hover:bg-hue-cyan/15 hover:border-hue-cyan/30",
        indigo:
          "bg-hue-indigo/10 text-hue-indigo border border-hue-indigo/20 hover:bg-hue-indigo/15 hover:border-hue-indigo/30",
      },
      size: {
        sm: "px-2 py-0.5 text-xs",
        default: "px-3 py-1 text-sm",
        lg: "px-4 py-1.5 text-sm",
      },
      rounded: {
        full: "rounded-full",
        md: "rounded-md",
        sm: "rounded-sm",
        square: "rounded-none",
      },
    },
    defaultVariants: {
      rounded: "full",
      variant: "default",
      size: "default",
    },
  }
);

function Badge({
  className,
  variant,
  size,
  rounded,
  asChild = false,
  ...props
}: React.ComponentProps<"span"> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : ("span" as React.ElementType);

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant, size, rounded }), className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
